import React, { useEffect, useState } from 'react';
import "../global.scss";
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { userAtom } from '../atom';
import { useSigningMethods } from '../components/helperfunction';
import { notification } from 'antd';
import SitsComLogo from '../components/SitsComLogo';

function Signingforms() {
  const currentUser = useAtom(userAtom)?.[0];
  const nav = useNavigate();
  const [mode, setMode] = useState('signin');
  const { handleGoogleSignIn, handleMailSignIn, handleMailSignUp, sendResetEmail } = useSigningMethods();
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const [signInData, setSignInData] = useState({ email: '', password: '' });
  const [signUpData, setSignUpData] = useState({ name: '', email: '', password: '' });

  useEffect(() => {
    document.title = 'Sign in — SitsCom';
  }, []);

  useEffect(() => {
    if (currentUser) nav('/home');
  }, [currentUser, nav]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await handleMailSignIn(signInData.email, signInData.password);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await handleMailSignUp(signUpData.email, signUpData.password, signUpData.name);
      notification.info({
        message: 'Verify Email',
        description: 'Verification email sent. Please verify and sign in.',
        duration: 5,
      });
      setMode('signin');
      setSignUpData({ name: '', email: '', password: '' });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = () => {
    if (resetEmail) {
      sendResetEmail(resetEmail);
      setResetOpen(false);
      setResetEmail('');
    }
  };

  return (
    <>
      {resetOpen && (
        <div className="reset-modal-overlay" onClick={() => setResetOpen(false)}>
          <div className="reset-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reset password</h3>
            <p>Enter your email and we'll send you a reset link.</p>
            <input
              type="email"
              className="input"
              placeholder="Email address"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
            />
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setResetOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleResetPassword}>Send link</button>
            </div>
          </div>
        </div>
      )}

      <div className="auth-page">
        <div className="auth-panel-left">
          <div className="auth-panel-bg">
            <div className="auth-gradient" />
            <div className="auth-grid" />
            <div className="auth-dots" />
          </div>
          <div className="auth-container">
            <div className="auth-card fade-in">
              <div className="auth-header">
                <h1>{mode === 'signin' ? 'Welcome back' : 'Create account'}</h1>
                <p>{mode === 'signin'
                  ? 'Sign in to access your saved papers'
                  : 'Join SitsCom to save and access resources'
                }</p>
              </div>

              <div className="auth-social">
                <button className="google-btn" onClick={handleGoogleSignIn} type="button">
                  <img src="https://img.icons8.com/color/48/google-logo.png" alt="Google" />
                  Continue with Google
                </button>
              </div>

              <div className="auth-divider">or</div>

              {mode === 'signin' ? (
                <form className="auth-form" onSubmit={handleSignIn}>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" required placeholder="you@example.com" value={signInData.email}
                      onChange={(e) => setSignInData({ ...signInData, email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Password</label>
                    <input type="password" required placeholder="Your password" value={signInData.password}
                      onChange={(e) => setSignInData({ ...signInData, password: e.target.value })} />
                  </div>
                  <div className="auth-link">
                    <button type="button" onClick={() => setResetOpen(true)}>Forgot password?</button>
                  </div>
                  <button type="submit" className="auth-submit" disabled={loading}>
                    {loading ? 'Signing in...' : 'Sign in'}
                  </button>
                </form>
              ) : (
                <form className="auth-form" onSubmit={handleSignUp}>
                  <div className="form-group">
                    <label>Name</label>
                    <input type="text" required placeholder="Your name" value={signUpData.name}
                      onChange={(e) => setSignUpData({ ...signUpData, name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" required placeholder="you@example.com" value={signUpData.email}
                      onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Password</label>
                    <input type="password" required placeholder="Create a password" value={signUpData.password}
                      onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })} />
                  </div>
                  <button type="submit" className="auth-submit" disabled={loading}>
                    {loading ? 'Creating account...' : 'Create account'}
                  </button>
                </form>
              )}

              <div className="auth-footer">
                {mode === 'signin' ? (
                  <>Don't have an account?{' '}
                    <button onClick={() => setMode('signup')}>Sign up</button>
                  </>
                ) : (
                  <>Already have an account?{' '}
                    <button onClick={() => setMode('signin')}>Sign in</button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="auth-panel-right">
          <div className="auth-panel-info">
            <div className="auth-panel-logo">
              <SitsComLogo size={48} />
            </div>
            <h2>Everything you need, in one place</h2>
            <p>
              Access past papers, lecture notes, books, and assignments for all
              COMSATS Lahore programs — completely free, forever.
            </p>
            <div className="auth-features">
              <div className="auth-feature">
                <div className="feature-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                </div>
                <div className="feature-text">
                  <strong>Course Materials</strong>
                  Past papers, notes, books, and quizzes
                </div>
              </div>
              <div className="auth-feature">
                <div className="feature-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                </div>
                <div className="feature-text">
                  <strong>Save Favorites</strong>
                  Bookmark subjects for quick access later
                </div>
              </div>
              <div className="auth-feature">
                <div className="feature-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <div className="feature-text">
                  <strong>Community Driven</strong>
                  Students contribute and share resources
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Signingforms;
